// components/views/profile/PaymentSetup.jsx
import React, { Component } from 'react';
import { HTTPService } from '../../../services/httpService';

const API = new HTTPService();

class PaymentSetup extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      loading: false,
      error: null,
      setupInProgress: false,
      stripeAccountLink: null
    };
  }

  componentDidMount() {
    this.checkStripeAccountStatus();
  }

  checkStripeAccountStatus = async () => {
    const { installer } = this.props;
    
    if (installer && installer.stripe_account_id && !installer.onboarding_complete) {
      // Account exists but onboarding not complete - might need to continue setup
      this.setState({ setupInProgress: true });
    }
  };

  handleCreateStripeAccount = async () => {
    this.setState({ loading: true, error: null });

    try {
      // Create Stripe Express account for the installer
      const response = await API.post('/stripe/create-express-account', {
        installerId: this.props.installer.Installer_id
      });

      if (response.data.error) {
        throw new Error(response.data.message);
      }

      // Redirect to Stripe onboarding
      if (response.data.accountLinkUrl) {
        window.location.href = response.data.accountLinkUrl;
      } else {
        throw new Error('Failed to create account setup link');
      }

    } catch (error) {
      console.error('Stripe account creation error:', error);
      this.setState({
        loading: false,
        error: error.response?.data?.message || 'Failed to create payment account'
      });
    }
  };

  handleContinueOnboarding = async () => {
    this.setState({ loading: true, error: null });

    try {
      // Create account link to continue onboarding
      const response = await API.post('/stripe/continue-onboarding', {
        installerId: this.props.installer.Installer_id
      });

      if (response.data.error) {
        throw new Error(response.data.message);
      }

      // Redirect to continue Stripe onboarding
      if (response.data.accountLinkUrl) {
        window.location.href = response.data.accountLinkUrl;
      } else {
        throw new Error('Failed to create account setup link');
      }

    } catch (error) {
      console.error('Stripe onboarding continuation error:', error);
      this.setState({
        loading: false,
        error: error.response?.data?.message || 'Failed to continue account setup'
      });
    }
  };

  render() {
    const { installer } = this.props;
    const { loading, error } = this.state;

    if (!installer) {
      return (
        <div className="alert alert-warning">
          <p className="mb-0">Unable to load account information. Please refresh the page.</p>
        </div>
      );
    }

    // Account setup complete
    if (installer.stripe_account_id && installer.onboarding_complete) {
      const { stripeCapabilities } = this.props;
      const hasCapabilityData = stripeCapabilities && Object.keys(stripeCapabilities).length > 0;
      const hasCardPayments = hasCapabilityData && stripeCapabilities.card_payments === 'active';
      const hasACH = hasCapabilityData && stripeCapabilities.us_bank_account_ach_payments === 'active';

      return (
        <div className="payment-setup-complete">
          <div className="d-flex align-items-center">
            <span className="badge badge-success mr-2">
              <i className="fa fa-check mr-1"></i> Connected
            </span>
            <span className="text-muted">Your payment account is active</span>
          </div>

          {hasCapabilityData && (
            <div className="mt-2">
              <small className="text-muted d-block mb-1">Payment Capabilities:</small>
              <span className={`badge ${hasCardPayments ? 'badge-success' : 'badge-danger'} mr-2`}>
                <i className={`fa ${hasCardPayments ? 'fa-check' : 'fa-times'} mr-1`}></i>
                Card Payments
              </span>
              <span className={`badge ${hasACH ? 'badge-success' : 'badge-danger'} mr-2`}>
                <i className={`fa ${hasACH ? 'fa-check' : 'fa-times'} mr-1`}></i>
                ACH Bank Transfers
              </span>

              {!hasACH && (
                <div className="alert alert-warning mt-2 mb-0">
                  <small>
                    <i className="fa fa-exclamation-triangle mr-1"></i>
                    ACH Bank Transfers capability is required to create invoices.
                    Click <strong>Manage Account</strong> above to update your Stripe settings,
                    or contact <a href="mailto:support@jobvious.com">support@jobvious.com</a> for assistance.
                  </small>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Account created but onboarding not complete
    if (installer.stripe_account_id && !installer.onboarding_complete) {
      return (
        <div className="payment-setup-incomplete">
          <div className="alert alert-warning">
            <div className="d-flex align-items-center">
              <div className="mr-3">
                <div className="bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                  !
                </div>
              </div>
              <div>
                <h6 className="alert-heading mb-1">Payment Setup In Progress</h6>
                <p className="mb-0">Your payment account needs additional information to receive payments.</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger">
              <h6>Setup Error</h6>
              <p className="mb-0">{error}</p>
            </div>
          )}

          <div className="mb-3">
            <h6>Complete Your Setup</h6>
            <p className="text-muted">
              You started the payment account setup but didn't finish. Click below to continue 
              where you left off and complete your banking information.
            </p>
          </div>

          <button 
            className="btn btn-primary"
            onClick={this.handleContinueOnboarding}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                Loading...
              </>
            ) : (
              'Continue Payment Setup'
            )}
          </button>
        </div>
      );
    }

    // No Stripe account - need to create one
    return (
      <div className="payment-setup-required">
        <div className="alert alert-info">
          <div className="d-flex align-items-center">
            <div className="mr-3">
              <div className="bg-info text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                $
              </div>
            </div>
            <div>
              <h6 className="alert-heading mb-1">Payment Account Required</h6>
              <p className="mb-0">Connect your bank account to receive job payments instantly.</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <h6>Setup Error</h6>
            <p className="mb-0">{error}</p>
          </div>
        )}

        <div className="row mb-4">
          <div className="col-md-6">
            <h6>What You'll Need</h6>
            <ul className="list-unstyled">
              <li>• Business or personal bank account</li>
              <li>• Routing and account numbers</li>
              <li>• Business tax ID (if applicable)</li>
              <li>• Valid government-issued ID</li>
            </ul>
          </div>
          <div className="col-md-6">
            <h6>Security & Protection</h6>
            <ul className="list-unstyled">
              <li>• Bank-level encryption</li>
              <li>• PCI DSS compliant</li>
              <li>• Secure data transmission</li>
              <li>• Fraud monitoring</li>
            </ul>
          </div>
        </div>

        <div className="mb-3">
          <h6>How It Works</h6>
          <div className="row">
            <div className="col-md-4 text-center mb-3">
              <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style={{width: '50px', height: '50px'}}>
                1
              </div>
              <h6>Connect Account</h6>
              <p className="text-muted small">Securely link your bank account through our payment processor</p>
            </div>
            <div className="col-md-4 text-center mb-3">
              <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style={{width: '50px', height: '50px'}}>
                2
              </div>
              <h6>Complete Jobs</h6>
              <p className="text-muted small">Finish work and mark jobs as complete in your dashboard</p>
            </div>
            <div className="col-md-4 text-center mb-3">
              <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style={{width: '50px', height: '50px'}}>
                3
              </div>
              <h6>Get Paid</h6>
              <p className="text-muted small">Receive payment instantly - no waiting for invoices or checks</p>
            </div>
          </div>
        </div>

        <div className="text-center">
          <button 
            className="btn btn-primary btn-lg"
            onClick={this.handleCreateStripeAccount}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                Creating Account...
              </>
            ) : (
              'Set Up Payment Account'
            )}
          </button>
          
          <p className="text-muted mt-2 small">
            You'll be redirected to our secure payment processor to complete setup
          </p>
        </div>

        {installer.is_trial && (
          <div className="alert alert-warning mt-3">
            <h6 className="alert-heading">Trial Account Notice</h6>
            <p className="mb-0">
              Payment setup is required before you can complete any jobs and receive payment. 
              This is a one-time setup that takes about 3-5 minutes.
            </p>
          </div>
        )}
      </div>
    );
  }
}

export default PaymentSetup;